/**
 * Helper für Projekt-Export & -Import.
 *
 * Export lädt JSON via GET-Endpoint und triggert einen Browser-Download.
 * Import sendet die ausgewählte Datei als JSON-Body an den Import-Endpoint.
 *
 * Größenlimit: ~50 MB pro Datei (knapp unter dem Server-Limit von 60 MB).
 */
const MAX_IMPORT_BYTES = 50 * 1024 * 1024;

export type ProjectExportSummary = { name?: string };

function sanitizeFileNamePart(input: string): string {
  return input
    .normalize("NFKD")
    .replace(/[^\w\s.-]+/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .slice(0, 80) || "projekt";
}

export async function downloadProjectExport(
  projectId: number,
  projectName: string,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/export`, {
    credentials: "include",
    signal,
  });
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const today = new Date().toISOString().slice(0, 10);
    const fileName = `bauwerkplus_${sanitizeFileNamePart(projectName)}_${today}.json`;
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export async function importProjectFromFile(
  file: File,
  signal?: AbortSignal,
): Promise<{ id: number; name?: string }> {
  if (file.size > MAX_IMPORT_BYTES) {
    throw new Error(
      `Datei ist zu groß (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximal ~50 MB sind erlaubt.`,
    );
  }
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("Datei ist keine gültige JSON-Datei.");
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("Datei hat ein ungültiges Format.");
  }
  const obj = parsed as Record<string, unknown>;
  if (obj.schemaVersion !== 1) {
    throw new Error("Diese Datei stammt aus einer nicht unterstützten Version.");
  }
  const projectName =
    obj.project && typeof obj.project === "object"
      ? ((obj.project as Record<string, unknown>).name as string | undefined)
      : undefined;

  const res = await fetch("/api/projects/import", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: text,
    credentials: "include",
    signal,
  });
  if (!res.ok) {
    if (res.status === 413) {
      throw new Error("Datei ist zu groß für den Server.");
    }
    const msg = (await res.text()) || res.statusText;
    throw new Error(msg);
  }
  const json = (await res.json()) as { id: number };
  return { id: json.id, name: projectName };
}
